import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, ShieldCheck, Users, UsersRound } from 'lucide-react';
import type { CustomerGroupToken } from '@sleed/bizcommerce-api';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { useCustomerGroupsList } from '~/hooks/use-customer-groups';
import { tokenToForm, type CustomerGroupFormValues } from '~/lib/schemas/customer-group';
import { CustomerGroupEditDialog } from './group-edit-dialog';
import { CriteriaDialog } from './criteria-dialog';
import { MembersDialog } from './members-dialog';
import { GroupMemberOverridesDialog } from './group-member-overrides-dialog';

/**
 * Customer groups (Ομάδες Πελατών) — parity with the legacy admin grid. Per-row actions open the
 * dynamic membership criteria editor and a members preview. Delete is intentionally omitted (the
 * legacy grid disables it).
 */
export function CustomerGroupsGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useCustomerGroupsList();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<CustomerGroupFormValues | null>(null);
	const [criteriaFor, setCriteriaFor] = useState<CustomerGroupToken | null>(null);
	const [membersFor, setMembersFor] = useState<CustomerGroupToken | null>(null);
	const [overridesFor, setOverridesFor] = useState<CustomerGroupToken | null>(null);

	const columns = useMemo<SystemGridColumn<CustomerGroupToken>[]>(
		() => [
			{
				key: 'code',
				header: t('customers.groups.columns.code'),
				width: '200px',
				sortValue: o => o.cd ?? '',
				exportValue: o => o.cd ?? '',
				render: o => <span className="truncate font-mono text-xs text-foreground-muted">{o.cd || '—'}</span>
			},
			{
				key: 'name',
				header: t('customers.groups.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.nm ?? '',
				exportValue: o => o.nm ?? '',
				render: o => (
					<span className="truncate text-sm font-medium text-foreground" title={o.nm ?? ''}>
						{o.nm || '—'}
					</span>
				)
			},
			{
				key: 'description',
				header: t('customers.groups.columns.description'),
				width: 'minmax(0,1.5fr)',
				sortValue: o => o.dsc ?? '',
				exportValue: o => o.dsc ?? '',
				render: o => (
					<span className="truncate text-sm text-foreground-muted" title={o.dsc ?? ''}>
						{o.dsc || '—'}
					</span>
				)
			}
		],
		[t]
	);

	const rowActions = (o: CustomerGroupToken) => (
		<>
			<Button
				variant="ghost"
				size="icon"
				aria-label={t('customers.groups.criteria.title')}
				title={t('customers.groups.criteria.title')}
				onClick={() => setCriteriaFor(o)}>
				<Filter size={16} />
			</Button>
			<Button
				variant="ghost"
				size="icon"
				aria-label={t('customers.groups.members.title')}
				title={t('customers.groups.members.title')}
				disabled={!o.expr_id}
				onClick={() => setMembersFor(o)}>
				<Users size={16} />
			</Button>
			<Button
				variant="ghost"
				size="icon"
				aria-label={t('customers.groups.memberOverrides.title')}
				title={t('customers.groups.memberOverrides.title')}
				onClick={() => setOverridesFor(o)}>
				<ShieldCheck size={16} />
			</Button>
		</>
	);

	return (
		<>
			<SystemGrid<CustomerGroupToken>
				gridId="customer-groups"
				title={t('customers.groups.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? o.cd ?? ''}
				columns={columns}
				searchPredicate={(o, q) => (o.nm ?? '').toLowerCase().includes(q) || (o.cd ?? '').toLowerCase().includes(q)}
				defaultSort={{ key: 'name', dir: 'asc' }}
				emptyIcon={<UsersRound size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('customers.groups.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={o => {
					setEditing(tokenToForm(o));
					setEditOpen(true);
				}}
				rowActions={rowActions}
				exportFilename="customer-groups"
			/>

			<CustomerGroupEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />

			<CriteriaDialog
				open={Boolean(criteriaFor)}
				onOpenChange={open => !open && setCriteriaFor(null)}
				groupId={criteriaFor?.id ?? null}
				groupName={criteriaFor?.nm ?? undefined}
			/>

			<MembersDialog
				open={Boolean(membersFor)}
				onOpenChange={open => !open && setMembersFor(null)}
				exprId={membersFor?.expr_id ?? null}
				groupName={membersFor?.nm ?? undefined}
			/>
			<GroupMemberOverridesDialog
				open={Boolean(overridesFor)}
				onOpenChange={open => !open && setOverridesFor(null)}
				groupId={overridesFor?.id ?? null}
				groupName={overridesFor?.nm ?? undefined}
			/>
		</>
	);
}
